import type { WorkbookDoc } from '@bloo-team/api-client'

/**
 * The workbook's run mode — Off / Streamed / Scheduled — as the Settings tab
 * reads, edits and writes it (docs/detections.md §The workbook page).
 *
 * Wire shapes captured 2026-09-29 from twelve versions of a real workbook
 * (docs/inventory/workbook-blocks-capture.md §Workbook meta, versions and
 * schedules):
 *
 *   Off        type standard        cron null                 cron_obj {}
 *   Streamed   type auto-scheduled  cron null                 cron_obj {}
 *   Daily      type scheduled       "16 12 * * *"             {cronType:'daily', pick_time, timeZone}
 *   Weekly     type scheduled       "58 12 * * TUE,WED"       {… cronType:'weekly', week_selected}
 *   Monthly    type scheduled       "58 12 12 JAN,MAR *"      {… cronType:'monthly', month_selected, monthly_date_selected}
 *   Custom     type scheduled       "*\/20 * * * *"           {}
 *
 * Always UTC (`schedule_timezone: 'UTC'` — sent with Scheduled only — and `timeZone: 'UTC'`); query_timezone
 * is never written here.
 */

export type ScheduleMode = 'off' | 'streamed' | 'scheduled'
export type CronKind = 'daily' | 'weekly' | 'monthly' | 'custom'

export const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const
export const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const

/** The run-mode editor's state. Builder fields are kept across kind switches. */
export interface RunSchedule {
  mode: ScheduleMode
  kind: CronKind
  /** HH:mm, UTC. */
  time: string
  /** MON..SUN, week order. */
  days: string[]
  /** 1..31. */
  dayOfMonth: number
  /** JAN..DEC, calendar order. */
  months: string[]
  /** Custom: the cron text as typed. */
  cron: string
}

const obj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
const strs = (v: unknown, order: readonly string[]): string[] =>
  Array.isArray(v) ? order.filter((x) => v.includes(x)) : []

export function modeOfType(type: string): ScheduleMode {
  return type === 'auto-scheduled' ? 'streamed' : type === 'scheduled' ? 'scheduled' : 'off'
}

/**
 * The schedule a document opens with. A scheduled doc whose cron_obj names
 * daily / weekly / monthly opens that builder from cron_obj; one with an
 * empty cron_obj (or no cronType) opens as Custom with its cron text.
 */
export function scheduleOf(doc: WorkbookDoc): RunSchedule {
  const r = doc.raw
  const type = typeof r.type === 'string' ? r.type : doc.type
  const cron = typeof r.cron === 'string' ? r.cron : ''
  const co = obj(r.cron_obj)
  const time = typeof co.pick_time === 'string' && timeLooksValid(co.pick_time) ? co.pick_time : '00:00'
  const dom = Number(co.monthly_date_selected)
  const base: RunSchedule = {
    mode: modeOfType(type),
    kind: 'custom',
    time,
    days: strs(co.week_selected, WEEK_DAYS),
    dayOfMonth: Number.isInteger(dom) && dom >= 1 && dom <= 31 ? dom : 1,
    months: strs(co.month_selected, MONTHS),
    cron,
  }
  if (co.cronType === 'daily' || co.cronType === 'weekly' || co.cronType === 'monthly') {
    return { ...base, kind: co.cronType }
  }
  return base
}

export function timeLooksValid(t: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(t)
}

/** Legal enough for save: 5 space-separated cron fields. */
export function cronLooksValid(cron: string): boolean {
  return cron.trim().split(/\s+/).length === 5
}

/**
 * What a Scheduled run mode still needs before Apply; empty = ready. Every
 * builder field is required: a time (HH:mm), at least one day (weekly), a day
 * of month 1–31 and at least one month (monthly), five cron fields (custom).
 */
export function scheduleProblems(s: RunSchedule): string[] {
  if (s.mode !== 'scheduled') return []
  if (s.kind === 'custom') return cronLooksValid(s.cron) ? [] : ['a cron with five fields']
  const problems: string[] = []
  if (!timeLooksValid(s.time)) problems.push('a time (HH:mm)')
  if (s.kind === 'weekly' && s.days.length === 0) problems.push('at least one day')
  if (s.kind === 'monthly') {
    if (!(s.dayOfMonth >= 1 && s.dayOfMonth <= 31)) problems.push('a day of month (1–31)')
    if (s.months.length === 0) problems.push('at least one month')
  }
  return problems
}

/** Can this schedule be applied as it stands? */
export function scheduleValid(s: RunSchedule): boolean {
  return scheduleProblems(s).length === 0
}

/** Minute and hour for the cron, unpadded ("12:05" → "5 12"), as the captured edits write them. */
function minuteHour(time: string): string {
  const [h, m] = time.split(':').map(Number)
  return `${m} ${h}`
}

export interface ScheduleWire {
  type: 'standard' | 'auto-scheduled' | 'scheduled'
  cron: string | null
  cron_obj: Record<string, unknown>
  /** Scheduled only: the backend refuses a standard / auto-scheduled save that
   * carries it ("Unable to schedule a workbook", seen 2026-09-29). */
  schedule_timezone?: 'UTC'
}

/** The four fields a run-mode change writes. */
export function scheduleWire(s: RunSchedule): ScheduleWire {
  const tz = 'UTC' as const
  if (s.mode === 'off') return { type: 'standard', cron: null, cron_obj: {} }
  if (s.mode === 'streamed') return { type: 'auto-scheduled', cron: null, cron_obj: {} }
  const at = { pick_time: s.time, timeZone: tz }
  switch (s.kind) {
    case 'daily':
      return { type: 'scheduled', cron: `${minuteHour(s.time)} * * *`, cron_obj: { cronType: 'daily', ...at }, schedule_timezone: tz }
    case 'weekly':
      return {
        type: 'scheduled',
        cron: `${minuteHour(s.time)} * * ${s.days.join(',')}`,
        cron_obj: { cronType: 'weekly', ...at, week_selected: [...s.days] },
        schedule_timezone: tz,
      }
    case 'monthly':
      return {
        type: 'scheduled',
        cron: `${minuteHour(s.time)} ${s.dayOfMonth} ${s.months.join(',')} *`,
        cron_obj: {
          cronType: 'monthly',
          month_selected: [...s.months],
          monthly_date_selected: s.dayOfMonth,
          ...at,
        },
        schedule_timezone: tz,
      }
    case 'custom':
      return { type: 'scheduled', cron: s.cron.trim().replace(/\s+/g, ' '), cron_obj: {}, schedule_timezone: tz }
  }
}

/**
 * The doc with a run mode applied: type / cron / cron_obj /
 * schedule_timezone only — query_timezone and every other field untouched.
 */
export function withSchedule(doc: WorkbookDoc, s: RunSchedule): WorkbookDoc {
  const w = scheduleWire(s)
  const { schedule_timezone: _tz, ...rest } = doc.raw
  // Off / Streamed: no schedule_timezone at all (the stored one is dropped too).
  return { ...doc, type: w.type, raw: { ...(w.type === 'scheduled' ? doc.raw : rest), ...w } }
}

/** Would applying `next` change anything the saved doc has? Compared on the fields each mode uses. */
export function scheduleEquals(a: RunSchedule, b: RunSchedule): boolean {
  if (a.mode !== b.mode) return false
  if (a.mode !== 'scheduled') return true
  if (a.kind !== b.kind) return false
  switch (a.kind) {
    case 'custom':
      return a.cron.trim().replace(/\s+/g, ' ') === b.cron.trim().replace(/\s+/g, ' ')
    case 'daily':
      return a.time === b.time
    case 'weekly':
      return a.time === b.time && a.days.join() === b.days.join()
    case 'monthly':
      return a.time === b.time && a.dayOfMonth === b.dayOfMonth && a.months.join() === b.months.join()
  }
}

/** Toggle one entry of an ordered set, keeping the canonical order (MON..SUN, JAN..DEC). */
export function toggleIn(list: readonly string[], item: string, order: readonly string[]): string[] {
  const next = list.includes(item) ? list.filter((x) => x !== item) : [...list, item]
  return order.filter((x) => next.includes(x))
}

/**
 * The version's commit message for a run-mode change — the legacy
 * Schedule_alert.js wording, verbatim ("<prev>" = off | streamed | scheduled).
 */
export function runModeCommitMessage(prev: ScheduleMode, next: RunSchedule): string {
  if (next.mode !== 'scheduled') return `Workbook execution changed from ${prev} to ${next.mode}.`
  const lead = prev === 'scheduled' ? 'Workbook execution scheduled' : `Workbook execution changed from ${prev} to scheduled`
  switch (next.kind) {
    case 'daily':
      return `${lead} at ${next.time} for everyday.`
    case 'weekly':
      return `${lead} at ${next.time} for ${next.days.join(', ')}.`
    case 'monthly':
      return `${lead} at ${next.time} for day ${next.dayOfMonth} of ${next.months.join(', ')}.`
    case 'custom':
      return `${lead} with custom cron ${next.cron.trim().replace(/\s+/g, ' ')}`
  }
}
